import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to throttle resize event listeners
 * Uses requestAnimationFrame to batch resize events and prevent excessive DOM reads
 * 
 * @param callback - Function to call on resize (throttled)
 * @param delay - Optional delay in ms (default: 0, uses requestAnimationFrame)
 */
export function useThrottledResize(
  callback: () => void,
  delay: number = 0
): void {
  const rafRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const throttledCallback = useCallback(() => {
    if (delay > 0) {
      // Use timeout-based throttling
      if (timeoutRef.current) {
        return; // Already scheduled
      }
      
      timeoutRef.current = setTimeout(() => {
        callback();
        timeoutRef.current = null;
      }, delay);
    } else {
      // Use requestAnimationFrame for immediate but batched updates
      if (rafRef.current !== null) {
        return; // Already scheduled
      }
      
      rafRef.current = requestAnimationFrame(() => {
        callback();
        rafRef.current = null;
      });
    }
  }, [callback, delay]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    window.addEventListener('resize', throttledCallback, { passive: true });
    
    return () => {
      window.removeEventListener('resize', throttledCallback);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [throttledCallback]);
}
